import { createContext, ReactNode, useContext, useEffect, useMemo, useState } from "react";
import i18n, { applyHtmlDir } from "@/lib/i18n";

export type Language = "ar" | "en" | "tr";

type Area = Record<Language, string>;

export const languages: { code: Language; label: string; dir: "rtl" | "ltr" }[] = [
  { code: "ar", label: "AR", dir: "rtl" },
  { code: "en", label: "EN", dir: "ltr" },
  { code: "tr", label: "TR", dir: "ltr" },
];

export const areas: Area[] = [
  { en: "Beylikdüzü", ar: "بيليك دوزو", tr: "Beylikdüzü" },
  { en: "Esenyurt", ar: "اسنيورت", tr: "Esenyurt" },
  { en: "Başakşehir", ar: "باشاك شهير", tr: "Başakşehir" },
  { en: "Avcılar", ar: "افجلار", tr: "Avcılar" },
  { en: "Küçükçekmece", ar: "كوتشوك شكمجة", tr: "Küçükçekmece" },
  { en: "Büyükçekmece", ar: "بيوك شكمجة", tr: "Büyükçekmece" },
  { en: "Bahçeşehir", ar: "بهشة شهير", tr: "Bahçeşehir" },
  { en: "Avcılar Ambarlı", ar: "افجلار أمبارلي", tr: "Avcılar Ambarlı" },
  { en: "Florya", ar: "فلوريا", tr: "Florya" },
  { en: "Bakırköy", ar: "باكيركوي", tr: "Bakırköy" },
  { en: "Zeytinburnu", ar: "زيتون بورنو", tr: "Zeytinburnu" },
  { en: "Fatih", ar: "الفاتح", tr: "Fatih" },
  { en: "Eyüpsultan", ar: "أيوب سلطان", tr: "Eyüpsultan" },
  { en: "Kağıthane", ar: "كاغتهانة", tr: "Kağıthane" },
  { en: "Şişli", ar: "شيشلي", tr: "Şişli" },
  { en: "Beşiktaş", ar: "بشكتاش", tr: "Beşiktaş" },
  { en: "Sarıyer", ar: "سارير", tr: "Sarıyer" },
  { en: "Üsküdar", ar: "أوسكودار", tr: "Üsküdar" },
  { en: "Kadıköy", ar: "كاديكوي", tr: "Kadıköy" },
  { en: "Maltepe", ar: "مالتيبه", tr: "Maltepe" },
  { en: "Kartal", ar: "كارتال", tr: "Kartal" },
  { en: "Pendik", ar: "بندك", tr: "Pendik" },
  { en: "Tuzla", ar: "توزلا", tr: "Tuzla" },
  { en: "Ataşehir", ar: "أتاشهير", tr: "Ataşehir" },
  { en: "Ümraniye", ar: "عمرانية", tr: "Ümraniye" },
  { en: "Sancaktepe", ar: "سنجاق تبه", tr: "Sancaktepe" },
  { en: "Sultanbeyli", ar: "سلطان بيلي", tr: "Sultanbeyli" },
  { en: "Çekmeköy", ar: "جيكمكوي", tr: "Çekmeköy" },
];

export const otherArea: Area = {
  ar: "أخرى (يرجى التحديد)",
  en: "Other (please specify)",
  tr: "Diğer (lütfen belirtin)",
};

export const copy = {
  ar: {
    brand: "ISTANOVA",
    nav: { home: "الرئيسية", request: "طلب شقة", listings: "العروض المختارة", submit: "اعرض عقارك", contact: "تواصل", admin: "الإدارة" },
    heroTitle: "اعثر على شقتك في إسطنبول بسهولة وأمان",
    heroSubtitle: "املأ الطلب وسنرسل لك أفضل الخيارات المناسبة حسب ميزانيتك والمنطقة المطلوبة",
    cta: "اطلب شقة الآن",
    whatsApp: "تواصل عبر واتساب",
    trust: ["عروض محدثة يوميًا", "خيارات متعددة تناسب جميع الميزانيات", "دعم سريع عبر واتساب"],
    howTitle: "كيف نساعدك",
    steps: ["املأ الطلب", "استلم الخيارات المناسبة", "اختر الشقة المناسبة لك"],
    areasTitle: "اختر منطقة إسطنبول المفضلة",
    areasHint: "يمكنك تحديد منطقة في النموذج أو كتابة منطقة أخرى.",
    formTitle: "طلب شقة في إسطنبول",
    formSubtitle: "شاركنا احتياجاتك وسنرسل لك خيارات مناسبة بسرعة عبر واتساب.",
    fields: {
      fullName: "الاسم الكامل",
      whatsapp: "رقم واتساب",
      purpose: "الغرض",
      apartmentType: "نوع الشقة",
      furnished: "مفروشة",
      budget: "الميزانية",
      preferredArea: "المنطقة",
      moveInDate: "تاريخ الانتقال",
      residencyStatus: "حالة الإقامة",
      notes: "ملاحظات",
      otherArea: "اكتب المنطقة",
      otherType: "حدد النوع",
      advertiserType: "نوع المعلن",
      propertyType: "نوع العقار",
      price: "السعر",
      photos: "الصور",
      currency: "العملة",
      floorNumber: "رقم الطابق",
      buildingType: "نوع المبنى",
      hasGym: "نادي رياضي / صالة جيم",
      rooms: "عدد الغرف",
      bathrooms: "عدد الحمامات",
      kitchenType: "نوع المطبخ",
      balcony: "بلكون",
      sizeSqm: "المساحة (م²)",
      parking: "موقف سيارات",
      view: "الإطلالة",
      extraNotes: "ملاحظات إضافية",
      englishOnly: "يرجى استخدام أحرف وأرقام إنجليزية فقط",
      forgotPassword: "نسيت كلمة المرور؟",
      resetPasswordTitle: "إعادة تعيين كلمة المرور",
      resetEmailSent: "تم إرسال رابط إعادة التعيين إلى بريدك.",
      sendResetLink: "إرسال رابط الإعادة",
      backToSignIn: "عودة لتسجيل الدخول",
      notificationEmail: "بريد الإشعارات للإدارة",
      notificationEmailHint: "كل الإشعارات والطلبات سترسل إلى هذا البريد.",
      apartment: "شقة",
      villaProp: "فيلا",
      floor: "طابق",
      otherProp: "أخرى",
      standalone: "مبنى مستقل",
      complex: "مجمع سكني",
      openKitchen: "مفتوح",
      closedKitchen: "مغلق",
      semiFurnished: "مفروشة جزئيًا",
    },
    options: {
      rent: "إيجار",
      buy: "شراء",
      yes: "نعم",
      no: "لا",
      tourist: "سائح",
      residence: "إقامة",
      citizen: "مواطن تركي",
      select: "اختر",
      owner: "مالك مباشر",
      agency: "مكتب عقاري",
      company: "شركة",
      broker: "وسيط",
      apartment: "شقة",
      villa: "فيلا",
      duplex: "دوبلكس",
      shop: "محل",
      office: "مكتب",
      other: "أخرى"
    },
    submit: "إرسال الطلب",
    success: "تم استلام طلبك، سيتم التواصل معك قريبًا عبر واتساب وإرسال الخيارات المناسبة",
    required: "يرجى تعبئة الحقول المطلوبة بشكل صحيح.",
    listingsTitle: "عروض مختارة",
    listingsSubtitle: "هذه نخبة من عروضنا الحالية. تتوفر خيارات أكثر بكثير عند الطلب — تواصل معنا لإرسال الخيارات المناسبة لك.",
    emptyListings: "لا توجد عروض حاليًا. تواصل معنا عبر واتساب لإرسال الخيارات المناسبة.",
    listingLabels: {
      price: "السعر",
      area: "المنطقة",
      apartmentType: "نوع الشقة",
      furnished: "مفروشة",
      features: "المميزات",
      images: "الصور",
      status: "الحالة",
      available: "متاحة",
      reserved: "محجوزة",
      sold: "مباعة",
      yes: "نعم",
      no: "لا",
      nextImage: "الصورة التالية",
      previousImage: "الصورة السابقة",
      viewDetails: "عرض التفاصيل",
      backToListings: "العودة إلى العروض",
      askOnWhatsApp: "استفسر عبر واتساب",
      notFound: "العرض غير موجود.",
    },
    submitProperty: {
      title: "اعرض عقارك",
      subtitle: "أرسل تفاصيل عقارك ليصل إلى آلاف العملاء. ستتم مراجعة الطلب من قبل الإدارة قبل النشر.",
      authRequired: "يرجى تسجيل الدخول أولًا لإرسال عقارك.",
      signIn: "تسجيل الدخول",
      signUp: "إنشاء حساب",
      email: "البريد الإلكتروني",
      password: "كلمة المرور",
      photosHint: "يجب رفع 5 صور على الأقل و20 صورة كحد أقصى.",
      photosCount: (count: number) => `${count} صورة محددة`,
      photosRequired: "صور العقار مطلوبة (5 على الأقل).",
      photosTooMany: "الحد الأقصى 20 صورة.",
      sent: "تم استلام طلبك بنجاح. ستراجعه الإدارة قبل النشر.",
      submit: "إرسال للموافقة",
    },
    admin: {
      title: "لوحة الإدارة",
      subtitle: "إدارة العروض والطلبات وإعدادات الموقع.",
      signInTitle: "تسجيل دخول الإدارة",
      email: "البريد الإلكتروني",
      password: "كلمة المرور",
      signIn: "دخول",
      signUp: "إنشاء حساب",
      signOut: "تسجيل الخروج",
      noAccess: "هذا الحساب لا يملك صلاحية الإدارة.",
      addListing: "إضافة شقة",
      editListing: "تعديل الشقة",
      deleteListing: "حذف",
      save: "حفظ",
      cancel: "إلغاء",
      titleField: "العنوان",
      description: "الوصف",
      featuresHint: "اكتب كل ميزة في سطر منفصل",
      uploadImages: "رفع صور متعددة",
      removeImage: "حذف الصورة",
      saved: "تم الحفظ.",
      deleted: "تم الحذف.",
      tabListings: "العروض",
      tabSubmissions: "الطلبات",
      tabSettings: "الإعدادات",
      whatsappLabel: "رقم واتساب الإدارة",
      whatsappHint: "يستخدم في كل أزرار التواصل تلقائيًا.",
      whatsappSaved: "تم تحديث الرقم.",
      submissionsEmpty: "لا توجد طلبات حاليًا.",
      pending: "قيد المراجعة",
      approved: "مقبول",
      rejected: "مرفوض",
      approve: "قبول ونشر",
      reject: "رفض",
      previewPhotos: "معاينة الصور",
      submittedBy: "أرسله",
      tabRequests: "طلبات الشقق",
      requestsEmpty: "لا توجد طلبات حاليًا.",
      markContacted: "تم التواصل",
    },
    contactTitle: "تواصل معنا الآن",
    contactSubtitle: "فريقنا جاهز لمساعدتك بالعربية والإنجليزية والتركية.",
    email: "البريد الإلكتروني",
    footerText: "خدمة موثوقة للعثور على شقق للإيجار والشراء في إسطنبول.",
    seoTitle: "ISTANOVA | إيجار وشراء بسهولة في إسطنبول",
    seoDescription: "اطلب شقة في إسطنبول بسهولة واستلم أفضل الخيارات حسب ميزانيتك.",
  },
  en: {
    brand: "ISTANOVA",
    nav: { home: "Home", request: "Request Apartment", listings: "Featured Listings", submit: "List Your Property", contact: "Contact", admin: "Admin" },
    heroTitle: "Find your apartment in Istanbul easily and safely",
    heroSubtitle: "Fill the form and we will send you the best options based on your budget and preferred area",
    cta: "Submit Request",
    whatsApp: "WhatsApp Contact",
    trust: ["Daily updated listings", "Options for every budget", "Fast WhatsApp support"],
    howTitle: "How it works",
    steps: ["Fill the request", "Receive matching options", "Choose your apartment"],
    areasTitle: "Choose your preferred Istanbul area",
    areasHint: "Select an area in the form or write another area.",
    formTitle: "Request an apartment in Istanbul",
    formSubtitle: "Tell us what you need and we will send suitable options quickly via WhatsApp.",
    fields: {
      fullName: "Full Name",
      whatsapp: "WhatsApp Number",
      purpose: "Purpose",
      apartmentType: "Apartment Type",
      furnished: "Furnished",
      budget: "Budget",
      preferredArea: "Area",
      moveInDate: "Move-in Date",
      residencyStatus: "Residency Status",
      notes: "Notes",
      otherArea: "Write the area",
      otherType: "Specify type",
      advertiserType: "Advertiser type",
      propertyType: "Property type",
      price: "Price",
      photos: "Photos",
      currency: "Currency",
      floorNumber: "Floor number",
      buildingType: "Building type",
      hasGym: "Sports club / Gym",
      rooms: "Number of rooms",
      bathrooms: "Number of bathrooms",
      kitchenType: "Kitchen type",
      balcony: "Balcony",
      sizeSqm: "Size (m²)",
      parking: "Parking",
      view: "View",
      extraNotes: "Additional notes",
      englishOnly: "Please use English letters and numbers only",
      forgotPassword: "Forgot password?",
      resetPasswordTitle: "Reset password",
      resetEmailSent: "Password reset link sent to your email.",
      sendResetLink: "Send reset link",
      backToSignIn: "Back to sign in",
      notificationEmail: "Admin notification email",
      notificationEmailHint: "All notifications and requests are sent to this address.",
      apartment: "Apartment",
      villaProp: "Villa",
      floor: "Floor",
      otherProp: "Other",
      standalone: "Standalone building",
      complex: "Residential complex",
      openKitchen: "Open",
      closedKitchen: "Closed",
      semiFurnished: "Semi-furnished",
    },
    options: {
      rent: "Rent",
      buy: "Buy",
      yes: "Yes",
      no: "No",
      tourist: "Tourist",
      residence: "Residence",
      citizen: "Turkish citizen",
      select: "Select",
      owner: "Owner",
      agency: "Agency",
      company: "Company",
      broker: "Broker",
      apartment: "Apartment",
      villa: "Villa",
      duplex: "Duplex",
      shop: "Shop",
      office: "Office",
      other: "Other"
    },
    submit: "Submit Request",
    success: "Your request has been received. We will contact you soon via WhatsApp with suitable options",
    required: "Please complete the required fields correctly.",
    listingsTitle: "Featured Listings",
    listingsSubtitle: "These are a selection of our current properties. Many more options are available on request — contact us and we'll send the best matches.",
    emptyListings: "No featured listings yet. Contact us on WhatsApp and we'll send matching options.",
    listingLabels: {
      price: "Price",
      area: "Area",
      apartmentType: "Apartment type",
      furnished: "Furnished",
      features: "Features",
      images: "Images",
      status: "Status",
      available: "Available",
      reserved: "Reserved",
      sold: "Sold",
      yes: "Yes",
      no: "No",
      nextImage: "Next image",
      previousImage: "Previous image",
      viewDetails: "View details",
      backToListings: "Back to listings",
      askOnWhatsApp: "Ask on WhatsApp",
      notFound: "Listing not found.",
    },
    submitProperty: {
      title: "List Your Property",
      subtitle: "Submit your property details to reach thousands of buyers. Submissions are reviewed by our team before publishing.",
      authRequired: "Please sign in first to submit your property.",
      signIn: "Sign in",
      signUp: "Create account",
      email: "Email",
      password: "Password",
      photosHint: "Upload at least 5 photos and at most 20.",
      photosCount: (count: number) => `${count} photos selected`,
      photosRequired: "Photos are required (minimum 5).",
      photosTooMany: "Maximum 20 photos allowed.",
      sent: "Your submission was received. Our team will review it before publishing.",
      submit: "Submit for review",
    },
    admin: {
      title: "Admin dashboard",
      subtitle: "Manage listings, review submissions, and update site settings.",
      signInTitle: "Admin sign in",
      email: "Email",
      password: "Password",
      signIn: "Sign in",
      signUp: "Create account",
      signOut: "Sign out",
      noAccess: "This account does not have admin access.",
      addListing: "Add apartment",
      editListing: "Edit apartment",
      deleteListing: "Delete",
      save: "Save",
      cancel: "Cancel",
      titleField: "Title",
      description: "Description",
      featuresHint: "Enter one feature per line",
      uploadImages: "Upload multiple images",
      removeImage: "Remove image",
      saved: "Saved.",
      deleted: "Deleted.",
      tabListings: "Listings",
      tabSubmissions: "Submissions",
      tabSettings: "Settings",
      whatsappLabel: "Admin WhatsApp number",
      whatsappHint: "Used everywhere across the site automatically.",
      whatsappSaved: "Number updated.",
      submissionsEmpty: "No submissions yet.",
      pending: "Pending",
      approved: "Approved",
      rejected: "Rejected",
      approve: "Approve & publish",
      reject: "Reject",
      previewPhotos: "Preview photos",
      submittedBy: "Submitted by",
      tabRequests: "Apartment requests",
      requestsEmpty: "No requests yet.",
      markContacted: "Mark as contacted",
    },
    contactTitle: "Contact us now",
    contactSubtitle: "Our team is ready to help in Arabic, English, and Turkish.",
    email: "Email",
    footerText: "Trusted service for finding Istanbul apartments to rent or buy.",
    seoTitle: "ISTANOVA | Rent & Buy Apartments in Istanbul Easily",
    seoDescription: "Request an apartment in Istanbul and receive the best options based on your budget.",
  },
  tr: {
    brand: "ISTANOVA",
    nav: { home: "Ana Sayfa", request: "Daire Talebi", listings: "Öne Çıkan İlanlar", submit: "İlan Ver", contact: "İletişim", admin: "Yönetim" },
    heroTitle: "İstanbul’da güvenli ve kolay şekilde dairenizi bulun",
    heroSubtitle: "Formu doldurun, bütçenize ve bölgenize uygun en iyi seçenekleri size gönderelim",
    cta: "Talep Gönder",
    whatsApp: "WhatsApp İletişim",
    trust: ["Günlük güncellenen ilanlar", "Her bütçeye uygun seçenekler", "WhatsApp üzerinden hızlı destek"],
    howTitle: "Nasıl çalışır",
    steps: ["Formu doldurun", "Uygun seçenekleri alın", "Size en uygun daireyi seçin"],
    areasTitle: "Tercih ettiğiniz İstanbul bölgesini seçin",
    areasHint: "Formda bir bölge seçebilir veya başka bir bölge yazabilirsiniz.",
    formTitle: "İstanbul’da daire talebi oluşturun",
    formSubtitle: "İhtiyacınızı paylaşın, size uygun seçenekleri WhatsApp üzerinden hızlıca gönderelim.",
    fields: {
      fullName: "Ad Soyad",
      whatsapp: "WhatsApp Numarası",
      purpose: "Amaç",
      apartmentType: "Daire Tipi",
      furnished: "Eşyalı",
      budget: "Bütçe",
      preferredArea: "Bölge",
      moveInDate: "Taşınma Tarihi",
      residencyStatus: "İkamet Durumu",
      notes: "Notlar",
      otherArea: "Bölgeyi yazın",
      otherType: "Türü belirtin",
      advertiserType: "İlan veren türü",
      propertyType: "Mülk türü",
      price: "Fiyat",
      photos: "Fotoğraflar",
      currency: "Para birimi",
      floorNumber: "Kat numarası",
      buildingType: "Bina tipi",
      hasGym: "Spor kulübü / Spor salonu",
      rooms: "Oda sayısı",
      bathrooms: "Banyo sayısı",
      kitchenType: "Mutfak tipi",
      balcony: "Balkon",
      sizeSqm: "Alan (m²)",
      parking: "Otopark",
      view: "Manzara",
      extraNotes: "Ek notlar",
      englishOnly: "Lütfen yalnızca İngilizce harf ve rakam kullanın",
      forgotPassword: "Şifremi unuttum?",
      resetPasswordTitle: "Şifre sıfırla",
      resetEmailSent: "Şifre sıfırlama bağlantısı e-postanıza gönderildi.",
      sendResetLink: "Sıfırlama bağlantısı gönder",
      backToSignIn: "Girişe dön",
      notificationEmail: "Yönetici bildirim e-postası",
      notificationEmailHint: "Tüm bildirimler ve talepler bu adrese gönderilir.",
      apartment: "Daire",
      villaProp: "Villa",
      floor: "Kat",
      otherProp: "Diğer",
      standalone: "Müstakil bina",
      complex: "Konut sitesi",
      openKitchen: "Açık",
      closedKitchen: "Kapalı",
      semiFurnished: "Yarı eşyalı",
    },
    options: {
      rent: "Kiralık",
      buy: "Satın alma",
      yes: "Evet",
      no: "Hayır",
      tourist: "Turist",
      residence: "İkamet",
      citizen: "Türk vatandaşı",
      select: "Seçin",
      owner: "Mülk sahibi",
      agency: "Emlak ofisi",
      company: "Şirket",
      broker: "Aracı",
      apartment: "Daire",
      villa: "Villa",
      duplex: "Dubleks",
      shop: "Dükkan",
      office: "Ofis",
      other: "Diğer"
    },
    submit: "Talebi Gönder",
    success: "Talebiniz alındı, kısa süre içinde WhatsApp üzerinden size uygun seçenekler gönderilecektir",
    required: "Lütfen gerekli alanları doğru şekilde doldurun.",
    listingsTitle: "Öne Çıkan İlanlar",
    listingsSubtitle: "Bunlar mevcut ilanlarımızdan bir seçkidir. Talep üzerine çok daha fazla seçenek mevcuttur — bizimle iletişime geçin, size uygun seçenekleri gönderelim.",
    emptyListings: "Henüz öne çıkan ilan yok. WhatsApp üzerinden bize ulaşın, size uygun seçenekleri gönderelim.",
    listingLabels: {
      price: "Fiyat",
      area: "Bölge",
      apartmentType: "Daire tipi",
      furnished: "Eşyalı",
      features: "Özellikler",
      images: "Fotoğraflar",
      status: "Durum",
      available: "Mevcut",
      reserved: "Rezerve",
      sold: "Satıldı",
      yes: "Evet",
      no: "Hayır",
      nextImage: "Sonraki fotoğraf",
      previousImage: "Önceki fotoğraf",
      viewDetails: "Detayları gör",
      backToListings: "İlanlara dön",
      askOnWhatsApp: "WhatsApp'tan sor",
      notFound: "İlan bulunamadı.",
    },
    submitProperty: {
      title: "İlan Ver",
      subtitle: "Mülk bilgilerinizi gönderin, binlerce alıcıya ulaşın. Gönderiler yayınlanmadan önce ekibimiz tarafından incelenir.",
      authRequired: "İlan göndermek için önce giriş yapın.",
      signIn: "Giriş yap",
      signUp: "Hesap oluştur",
      email: "E-posta",
      password: "Şifre",
      photosHint: "En az 5, en fazla 20 fotoğraf yükleyin.",
      photosCount: (count: number) => `${count} fotoğraf seçildi`,
      photosRequired: "Fotoğraflar zorunludur (en az 5).",
      photosTooMany: "En fazla 20 fotoğrafa izin verilir.",
      sent: "Gönderiniz alındı. Yayınlanmadan önce ekibimiz inceleyecek.",
      submit: "İncelemeye gönder",
    },
    admin: {
      title: "Yönetim paneli",
      subtitle: "İlanları yönetin, gönderileri inceleyin ve site ayarlarını güncelleyin.",
      signInTitle: "Yönetici girişi",
      email: "E-posta",
      password: "Şifre",
      signIn: "Giriş yap",
      signUp: "Hesap oluştur",
      signOut: "Çıkış yap",
      noAccess: "Bu hesabın yönetici yetkisi yok.",
      addListing: "Daire ekle",
      editListing: "Daireyi düzenle",
      deleteListing: "Sil",
      save: "Kaydet",
      cancel: "İptal",
      titleField: "Başlık",
      description: "Açıklama",
      featuresHint: "Her satıra bir özellik yazın",
      uploadImages: "Birden fazla fotoğraf yükle",
      removeImage: "Fotoğrafı kaldır",
      saved: "Kaydedildi.",
      deleted: "Silindi.",
      tabListings: "İlanlar",
      tabSubmissions: "Gönderiler",
      tabSettings: "Ayarlar",
      whatsappLabel: "Yönetici WhatsApp numarası",
      whatsappHint: "Sitedeki tüm iletişim butonlarında otomatik kullanılır.",
      whatsappSaved: "Numara güncellendi.",
      submissionsEmpty: "Henüz gönderi yok.",
      pending: "Beklemede",
      approved: "Onaylandı",
      rejected: "Reddedildi",
      approve: "Onayla ve yayınla",
      reject: "Reddet",
      previewPhotos: "Fotoğrafları önizle",
      submittedBy: "Gönderen",
      tabRequests: "Daire talepleri",
      requestsEmpty: "Henüz talep yok.",
      markContacted: "İletişime geçildi",
    },
    contactTitle: "Hemen iletişime geçin",
    contactSubtitle: "Ekibimiz Arapça, İngilizce ve Türkçe destek vermeye hazır.",
    email: "E-posta",
    footerText: "İstanbul’da kiralık ve satılık daire bulmak için güvenilir hizmet.",
    seoTitle: "ISTANOVA | İstanbul'da Kolay Kiralık ve Satılık",
    seoDescription: "İstanbul’da bütçenize uygun daireleri kolayca bulun.",
  },
} as const;

type LanguageContextValue = {
  language: Language;
  setLanguage: (language: Language) => void;
  t: (typeof copy)[Language];
  dir: "rtl" | "ltr";
};

const LanguageContext = createContext<LanguageContextValue | null>(null);

export const LanguageProvider = ({ children }: { children: ReactNode }) => {
  const [language, setLanguage] = useState<Language>(() => {
    const saved = window.localStorage.getItem("istanbul-real-estate-language") as Language | null;
    return saved && ["ar", "en", "tr"].includes(saved) ? saved : "ar";
  });

  useEffect(() => {
    const current = languages.find((item) => item.code === language) ?? languages[0];
    applyHtmlDir(language);
    if (i18n.language !== language) i18n.changeLanguage(language);
    document.title = copy[language].seoTitle;
    document.querySelector('meta[name="description"]')?.setAttribute("content", copy[language].seoDescription);
    window.localStorage.setItem("istanbul-real-estate-language", language);
    // currently used `current` to keep dir in scope for memo below
    void current;
  }, [language]);

  const value = useMemo(() => {
    const current = languages.find((item) => item.code === language) ?? languages[0];
    return { language, setLanguage, t: copy[language], dir: current.dir };
  }, [language]);

  return <LanguageContext.Provider value={value}>{children}</LanguageContext.Provider>;
};

export const useLanguage = () => {
  const context = useContext(LanguageContext);
  if (!context) {
    throw new Error("useLanguage must be used within LanguageProvider");
  }
  return context;
};
